import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import './visitantes.css';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import { useInternals } from '../../context/internalContext';
import ReactPaginate from 'react-paginate';
import Modal from '../../components/modal';
import ConfirmModal from '../../components/modal/confirmModal';
import { useUsers } from '../../context/usersContext';
import { faBan, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { normalizeUnitId } from '../../utils/unitScope';
import {
  getVisitorDisplayStatus,
  isFamilyVisitorType,
  normalizeVisitorAuthStatus,
} from '../../utils/visitorStatus';

const normalizePermissionText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const getVisitorUniqueKey = (visitor, fallbackIndex = '') =>
  String(
    visitor?.id
    || visitor?.cpf
    || `${visitor?.name || 'visitor'}-${fallbackIndex}`
  );

function ListVisitantes() {
  const { getVisitors, updateVisitor, respondVisitorAuthorization, getInternalById } = useInternals();
  const { userInfo, userUnitIds, effectiveUnitId } = useUsers();
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [priorityVisitors, setPriorityVisitors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchType, setSearchType] = useState('name');
  const [searchInput, setSearchInput] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedBlockVisitor, setSelectedBlockVisitor] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [selectedAuthVisitor, setSelectedAuthVisitor] = useState(null);
  const [authForm, setAuthForm] = useState({
    social_worker_authorization: 'Autorizado',
    social_worker_motive: '',
  });
  const [authErrors, setAuthErrors] = useState({});
  const [isSavingAuth, setIsSavingAuth] = useState(false);
  const [isBlockMotiveModalOpen, setIsBlockMotiveModalOpen] = useState(false);
  const [blockMotive, setBlockMotive] = useState('');
  const [blockMotiveError, setBlockMotiveError] = useState('');
  const [isSavingBlockMotive, setIsSavingBlockMotive] = useState(false);
  const [internalUnitMap, setInternalUnitMap] = useState({});

  const normalizedGroups = userInfo?.groups?.map((group) => normalizePermissionText(group)) || [];

  const canDiscipline = normalizedGroups.includes('DISCIPLINA');
  const canSocial = normalizedGroups.includes('SERVICO SOCIAL');
  const canAdministrative = normalizedGroups.includes('ADMINISTRATIVO');
  const canAuthorize = canSocial;
  const canBlockByProfile = canDiscipline || canAdministrative;
  const isUnitManagerViewOnly = ['GERENTE DE UNIDADE', 'DIRETORES', 'DUAP'].some((group) => normalizedGroups.includes(group));

  const toNumberOr = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const buildSearchParams = useCallback(() => {
    const params = {};
    const trimmedSearch = searchInput.trim();
    if (trimmedSearch) {
      params[searchType] = searchType === 'cpf' ? trimmedSearch.replace(/\D/g, '') : trimmedSearch;
    }
    const selectedUnit = normalizeUnitId(effectiveUnitId);
    if (selectedUnit !== null) {
      params.unit = selectedUnit;
    }
    return params;
  }, [searchInput, searchType, effectiveUnitId]);

  const fetchAllVisitorsByParams = useCallback(async (baseParams = {}) => {
    const pageSize = 100;
    const maxPages = 30;
    const collected = [];
    let offset = 0;
    let page = 0;

    while (page < maxPages) {
      const response = await getVisitors({
        ...baseParams,
        limit: pageSize,
        offset,
      });

      const batch = Array.isArray(response) ? response : response?.results ?? [];
      if (!batch.length) {
        break;
      }

      collected.push(...batch);

      const totalCount = Number(response?.count);
      if (Number.isFinite(totalCount) && collected.length >= totalCount) {
        break;
      }

      if (!response?.next && batch.length < pageSize) {
        break;
      }

      offset += pageSize;
      page += 1;
    }

    return collected;
  }, [getVisitors]);

  const loadPriorityVisitors = useCallback(async () => {
    try {
      const searchParams = buildSearchParams();

      const [pendingSocial] = await Promise.all([
        fetchAllVisitorsByParams({
          ...searchParams,
          social_worker_authorization: 'Sem resposta',
        }),
        // fetchAllVisitorsByParams({
        //   ...searchParams,
        //   authorize_entry: 'False',
        // }),
      ]);

      // const blocked = []; // descomentar acima para reativar bloqueados no topo

      return Array.from(
        new Map(
          [...pendingSocial]
            .filter(Boolean)
            .map((visitor, index) => [getVisitorUniqueKey(visitor, index), visitor])
        ).values()
      );
    } catch (error) {
      console.error('Erro ao carregar visitantes prioritários:', error);
      return [];
    }
  }, [buildSearchParams, fetchAllVisitorsByParams]);

  const fetchVisitors = useCallback(async () => {
    setIsLoading(true);
    try {
      const safeItemsPerPage = toNumberOr(itemsPerPage, 10);
      const safeCurrentPage = toNumberOr(currentPage, 0);
      const offset = safeCurrentPage * safeItemsPerPage;
      const params = { limit: safeItemsPerPage, offset, ...buildSearchParams() };

      const response = await getVisitors(params);
      const visitorList = Array.isArray(response)
        ? response
        : response?.results ?? [];

      setVisitors(visitorList);
      setTotalItems(response?.count ?? visitorList.length);
    } catch (error) {
      console.error('Erro ao carregar visitantes:', error);
      setVisitors([]);
    } finally {
      setIsLoading(false);
    }
  }, [buildSearchParams, currentPage, getVisitors, itemsPerPage]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // 1. Carrega pendentes primeiro para aparecerem no topo antes da lista normal
      const prioritized = await loadPriorityVisitors();
      if (!cancelled) setPriorityVisitors(prioritized);

      // 2. Depois carrega a listagem paginada
      if (!cancelled) await fetchVisitors();
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [loadPriorityVisitors, fetchVisitors]);

  const extractInternalId = (internal) => {
    if (internal === null || internal === undefined) return null;
    if (typeof internal === 'object') return internal.id ?? null;
    return internal;
  };

  const extractInternalUnitId = (internal) =>
    normalizeUnitId(
      internal?.unit?.id ??
      internal?.unit_id ??
      internal?.unit ??
      internal?.cell?.pavilion?.unit?.id ??
      internal?.cell?.pavilion?.unit ??
      internal?.pavilion?.unit?.id ??
      internal?.pavilion?.unit
    );

  useEffect(() => {
    const visitorsForResolution = [
      ...(Array.isArray(visitors) ? visitors : []),
      ...(Array.isArray(priorityVisitors) ? priorityVisitors : []),
    ];

    const unresolvedInternalIds = [
      ...new Set(
        visitorsForResolution.flatMap((visitor) => {
          const internals = Array.isArray(visitor?.internals) ? visitor.internals : [];
          return internals
            .map((internal) => {
              const directUnitId = extractInternalUnitId(internal);
              if (directUnitId !== null) return null;
              return extractInternalId(internal);
            })
            .filter((internalId) => internalId !== null && internalId !== undefined && String(internalId).trim() !== '');
        })
      ),
    ].filter(
      (internalId) => !Object.prototype.hasOwnProperty.call(internalUnitMap, String(internalId))
    );

    if (!unresolvedInternalIds.length) {
      return;
    }

    let cancelled = false;

    const resolveInternalUnits = async () => {
      const updates = {};

      await Promise.all(
        unresolvedInternalIds.map(async (internalId) => {
          try {
            const internalDetails = await getInternalById(internalId);
            const resolvedUnitId = normalizeUnitId(
              internalDetails?.unit?.id ??
              internalDetails?.unit_id ??
              internalDetails?.unit ??
              internalDetails?.cell?.pavilion?.unit?.id ??
              internalDetails?.cell?.pavilion?.unit ??
              internalDetails?.pavilion?.unit?.id ??
              internalDetails?.pavilion?.unit
            );
            updates[String(internalId)] = resolvedUnitId;
          } catch (error) {
            updates[String(internalId)] = null;
          }
        })
      );

      if (!cancelled && Object.keys(updates).length > 0) {
        setInternalUnitMap((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    };

    resolveInternalUnits();

    return () => {
      cancelled = true;
    };
  }, [visitors, priorityVisitors, getInternalById, internalUnitMap]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.actions-container')) {
        setOpenMenuIndex(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    setSearchInput('');
    setCurrentPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
    setCurrentPage(0);
  };

  const handleConfirmBlock = (visitor) => {
    setSelectedBlockVisitor(visitor);
    if (visitor?.authorize_entry === true) {
      setBlockMotive(visitor?.discipline_motive || '');
      setBlockMotiveError('');
      setIsBlockMotiveModalOpen(true);
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setSelectedBlockVisitor(null);
    setIsConfirmModalOpen(false);
  };

  const closeBlockMotiveModal = () => {
    setIsBlockMotiveModalOpen(false);
    setBlockMotive('');
    setBlockMotiveError('');
    setSelectedBlockVisitor(null);
  };

  const confirmBlockAction = async () => {
    if (!selectedBlockVisitor) return;
    try {
      await updateVisitor(selectedBlockVisitor.id, {
        authorize_entry: true,
      });
      closeConfirmModal();
      await Promise.all([
        fetchVisitors(),
        loadPriorityVisitors().then(setPriorityVisitors),
      ]);
    } catch (error) {
      console.error('Erro ao atualizar autorização do visitante:', error);
    }
  };

  const confirmBlockWithMotive = async () => {
    if (!selectedBlockVisitor) return;
    const trimmedMotive = blockMotive.trim();
    if (!trimmedMotive) {
      setBlockMotiveError('Informe o motivo do bloqueio.');
      return;
    }

    setIsSavingBlockMotive(true);
    try {
      await updateVisitor(selectedBlockVisitor.id, {
        authorize_entry: false,
        discipline_motive: trimmedMotive,
      });
      closeBlockMotiveModal();
      await Promise.all([
        fetchVisitors(),
        loadPriorityVisitors().then(setPriorityVisitors),
      ]);
    } catch (error) {
      console.error('Erro ao bloquear visitante:', error);
    } finally {
      setIsSavingBlockMotive(false);
    }
  };

  const handleOpenAuthModal = (visitor) => {
    setSelectedAuthVisitor(visitor);
    setAuthForm({
      social_worker_authorization: visitor.social_worker_authorization || 'Sem resposta',
      social_worker_motive: visitor.social_worker_motive || '',
    });
    setAuthErrors({});
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setSelectedAuthVisitor(null);
    setAuthErrors({});
    setIsAuthModalOpen(false);
  };

  const handleAuthChange = (event) => {
    const { name, value } = event.target;
    setAuthForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveAuthorization = async () => {
    if (!selectedAuthVisitor) return;
    const canRespondSocialNow = canSocial;

    const errors = {};
    if (
      canRespondSocialNow &&
      authForm.social_worker_authorization === 'Não autorizado' &&
      !authForm.social_worker_motive.trim()
    ) {
      errors.social_worker_motive = 'Informe o motivo para recusar.';
    }

    if (Object.keys(errors).length > 0) {
      setAuthErrors(errors);
      return;
    }

    const payload = {};

    if (canRespondSocialNow) {
      payload.social_worker_authorization = authForm.social_worker_authorization || 'Sem resposta';
      payload.social_worker_motive =
        authForm.social_worker_authorization === 'Não autorizado'
          ? authForm.social_worker_motive
          : '';
    }

    if (!Object.keys(payload).length) {
      return;
    }

    setIsSavingAuth(true);
    try {
      await respondVisitorAuthorization(selectedAuthVisitor.id, payload);
      const results = await Promise.all([
        fetchVisitors(),
        loadPriorityVisitors(),
      ]);
      setPriorityVisitors(results[1] || []);
      closeAuthModal();
    } catch (error) {
      console.error('Erro ao atualizar autorização:', error);
    } finally {
      setIsSavingAuth(false);
    }
  };

  const confirmContext = selectedBlockVisitor
    ? {
      main: selectedBlockVisitor.authorize_entry
        ? 'Deseja bloquear este visitante?'
        : 'Deseja desbloquear este visitante?',
      secondary: selectedBlockVisitor.authorize_entry
        ? `Ao confirmar, ${selectedBlockVisitor.name || 'o visitante'} ficará impedido de realizar visitas até nova autorização.`
        : `Ao confirmar, ${selectedBlockVisitor.name || 'o visitante'} estará liberado para visitas.`,
      icon: selectedBlockVisitor.authorize_entry ? faBan : faUnlock,
      isBlocking: selectedBlockVisitor.authorize_entry,
    }
    : { main: '', secondary: '', icon: faBan, isBlocking: true };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(toNumberOr(selected, 0));
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(toNumberOr(event.target.value, 10));
    setCurrentPage(0);
  };

  const normalizeStatusValue = (value) => {
    return normalizeVisitorAuthStatus(value);
  };

  const canRespondSocialAuthorization = (visitor) =>
    canSocial && ['sem resposta', 'nao autorizado', 'autorizado'].includes(normalizeStatusValue(visitor?.social_worker_authorization));

  const canShowAuthorizationAction = (visitor) =>
    canAuthorize &&
    isFamilyVisitorType(visitor) &&
    canManageVisitorByUnit(visitor) &&
    canRespondSocialAuthorization(visitor);

  const canShowBlockAction = (visitor) =>
    (() => {
      if (!canBlockByProfile || isUnitManagerViewOnly) return false;

      if (!isFamilyVisitorType(visitor)) {
        // Visitantes sem tipo Familiar podem ser bloqueados/desbloqueados
        // independentemente da unidade selecionada.
        return true;
      }

      // Familiar: exige mesma unidade e autorização do serviço social.
      if (!canManageVisitorByUnit(visitor)) return false;
      const socialAuthorized = normalizeStatusValue(visitor?.social_worker_authorization) === 'autorizado';
      if (!socialAuthorized) return false;
      return true;
    })();

  const getVisitorStatus = (visitor) => getVisitorDisplayStatus(visitor);

  const getVisitorTypes = (visitor) => {
    const types = Array.isArray(visitor?.type_visitor) ? visitor.type_visitor : [];
    if (!types.length) return '-';
    return types.map((type) => type?.name || '-').join('\n');
  };

  const getVisitorUnitIds = (visitor) => {
    const internals = Array.isArray(visitor?.internals) ? visitor.internals : [];
    const unitIds = new Set();

    internals.forEach((internal) => {
      const directUnitId = extractInternalUnitId(internal);
      if (directUnitId !== null) {
        unitIds.add(String(directUnitId));
        return;
      }

      const internalId = extractInternalId(internal);
      if (internalId === null || internalId === undefined) return;

      const mappedUnitId = normalizeUnitId(internalUnitMap[String(internalId)]);
      if (mappedUnitId !== null) {
        unitIds.add(String(mappedUnitId));
      }
    });

    return unitIds;
  };

  const canManageVisitorByUnit = (visitor) => {
    if (userInfo?.is_superuser) {
      return true;
    }

    const selectedUnit = normalizeUnitId(effectiveUnitId);
    const allowedUnits = selectedUnit !== null
      ? new Set([String(selectedUnit)])
      : new Set(
        (userUnitIds || [])
          .map((unitId) => normalizeUnitId(unitId))
          .filter((unitId) => unitId !== null)
          .map((unitId) => String(unitId))
      );

    if (!allowedUnits.size) {
      return false;
    }

    const visitorInternals = Array.isArray(visitor?.internals) ? visitor.internals : [];
    if (!visitorInternals.length) {
      return false;
    }

    const visitorUnitIds = getVisitorUnitIds(visitor);
    if (!visitorUnitIds.size) {
      return false;
    }

    return [...visitorUnitIds].some((unitId) => allowedUnits.has(String(unitId)));
  };

  const getSortedVisitors = () => {
    const priorityMap = {
      Pendente: 0,
      Bloqueado: 1,
      'Não autorizado': 2,
      Autorizado: 3,
    };

    return [...visitors].sort((a, b) => {
      const statusA = getVisitorStatus(a);
      const statusB = getVisitorStatus(b);
      const priorityA = priorityMap[statusA.label] ?? 99;
      const priorityB = priorityMap[statusB.label] ?? 99;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return 0;
    });
  };

  const sortedVisitors = getSortedVisitors();
  const priorityMap = {
    Pendente: 0,
    Bloqueado: 1,
    'Não autorizado': 2,
    Autorizado: 3,
  };

  const prioritizedVisitors = [...priorityVisitors]
    .filter((visitor) => {
      const label = getVisitorStatus(visitor).label;
      return label === 'Pendente' || label === 'Bloqueado';
    })
    .sort((a, b) => {
      const statusA = getVisitorStatus(a);
      const statusB = getVisitorStatus(b);
      const priorityA = priorityMap[statusA.label] ?? 99;
      const priorityB = priorityMap[statusB.label] ?? 99;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return String(a?.name || '').localeCompare(String(b?.name || ''), 'pt-BR');
    });

  const prioritizedVisitorKeys = new Set(
    prioritizedVisitors.map((visitor, index) => getVisitorUniqueKey(visitor, index))
  );

  const visitorsForRender = currentPage === 0
    ? [
      ...prioritizedVisitors,
      ...sortedVisitors.filter(
        (visitor, index) => !prioritizedVisitorKeys.has(getVisitorUniqueKey(visitor, index))
      ),
    ]
    : sortedVisitors;

  const pageCount = Math.ceil(totalItems / itemsPerPage) || 0;
  const canRespondSelectedSocial = selectedAuthVisitor
    ? canRespondSocialAuthorization(selectedAuthVisitor)
    : canSocial;




  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container">
        <h4 className="title">Visitantes - Familiar</h4>

        <Titulo
          children={
            <form>
              <select
                id="searchType"
                value={searchType}
                onChange={handleSearchTypeChange}
              >
                <option value="name">Nome</option>
                <option value="cpf">CPF</option>
              </select>
              <div className="search-wrapper">
                <FormattedInput
                  type={searchType === 'cpf' ? 'cpf' : 'nome'}
                  id="searchInput"
                  name="searchInput"
                  placeholder="Pesquisar"
                  value={searchInput}
                  onChange={handleSearchChange}
                />
              </div>
            </form>
          }
        />

        <div className="content content-table">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF</th>
                <th>Internos Relacionados</th>
                <th>Tipo Visitante</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}>
                    Carregando visitantes...
                  </td>
                </tr>
              ) : visitorsForRender.length > 0 ? (
                visitorsForRender.map((visitor, index) => {
                  const status = getVisitorStatus(visitor);
                  return (
                    <tr key={getVisitorUniqueKey(visitor, index)}>
                      <td>{visitor.name || '-'}</td>
                      <td>{visitor.cpf || '-'}</td>
                      <td>
                        {visitor.internals?.map((item, index) => (
                          <div key={item.id}>
                            {item.name}
                            {index < visitor.internals.length - 1 && <br />}
                          </div>
                        ))}
                      </td>
                      <td style={{ whiteSpace: 'pre-wrap' }}>
                        {getVisitorTypes(visitor)}
                      </td>
                      <td>
                        <span className={`visitor-status ${status.className}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="td-table-actions">
                        <div className="actions-container">
                          <button
                            className="btn-actions"
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenMenuIndex(openMenuIndex === index ? null : index);
                            }}
                          >
                            ...
                          </button>
                          {openMenuIndex === index && (
                            <div className="actions-menu">
                              <button
                                type="button"
                                className="action-item"
                                onClick={() => {
                                  setOpenMenuIndex(null);
                                  if (visitor.id) {
                                    navigate(`/detalhes-visitantes/${visitor.id}`);
                                  }
                                }}
                              >
                                Detalhes
                              </button>
                              {canShowAuthorizationAction(visitor) && (
                                <button
                                  type="button"
                                  className="action-item"
                                  onClick={() => {
                                    setOpenMenuIndex(null);
                                    handleOpenAuthModal(visitor);
                                  }}
                                >
                                  Autorização
                                </button>
                              )}
                              {canShowBlockAction(visitor) && visitor.authorize_entry === true && (
                                <button
                                  type="button"
                                  className="action-item"
                                  onClick={() => {
                                    setOpenMenuIndex(null);
                                    handleConfirmBlock(visitor);
                                  }}
                                  style={{ color: 'var(--Vermelho)' }}
                                >
                                  Bloquear
                                </button>
                              )}
                              {canShowBlockAction(visitor) && visitor.authorize_entry === false && (
                                <button
                                  type="button"
                                  className="action-item"
                                  onClick={() => {
                                    setOpenMenuIndex(null);
                                    handleConfirmBlock(visitor);
                                  }}
                                  style={{ color: 'var(--verde)' }}
                                >
                                  Desbloquear
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}>
                    Nenhum visitante encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-options">
          <div>
            <label htmlFor="visitorItemsPerPage">Visitantes por página:</label>
            <select
              id="visitorItemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {pageCount > 1 && (
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              pageCount={pageCount}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handlePageClick}
              containerClassName={'pagination'}
              activeClassName={'activeNumber'}
              previousClassName={'prev'}
              nextClassName={'next'}
              pageClassName={'page'}
              breakClassName={'break-me'}
              forcePage={currentPage}
            />
          )}
        </div>

      </div>

      <Modal
        isOpen={isAuthModalOpen}
        closeModal={closeAuthModal}
        title="Atualizar autorizações"
      >
        <div className="auth-modal-grid">
          {canRespondSelectedSocial && (
            <div className="auth-column">
              <h4>Serviço Social</h4>
              {selectedAuthVisitor?.social_worker_authorization && (
                <p className="auth-status-info">
                  Status atual: <strong>{selectedAuthVisitor.social_worker_authorization}</strong>
                </p>
              )}
              <select
                name="social_worker_authorization"
                value={authForm.social_worker_authorization}
                onChange={handleAuthChange}
              >
                <option value="Sem resposta">Sem resposta</option>
                <option value="Autorizado">Autorizado</option>
                <option value="Não autorizado">Não autorizado</option>
              </select>

              {authForm.social_worker_authorization === 'Não autorizado' && (
                <div className="form-group auth-textarea-group">
                  <label>Motivo (Serviço Social)</label>
                  <textarea
                    name="social_worker_motive"
                    value={authForm.social_worker_motive}
                    onChange={handleAuthChange}
                  />
                  {authErrors.social_worker_motive && (
                    <span className="visitante-error">{authErrors.social_worker_motive}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button
            className="btn-visitor-secondary"
            onClick={handleSaveAuthorization}
            disabled={isSavingAuth}
          >
            {isSavingAuth ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isBlockMotiveModalOpen}
        closeModal={closeBlockMotiveModal}
        title="Bloquear visitante"
      >
        <div className="form-group auth-textarea-group">
          <label>Motivo do bloqueio (Disciplina)</label>
          <textarea
            value={blockMotive}
            onChange={(event) => {
              setBlockMotive(event.target.value);
              if (blockMotiveError) {
                setBlockMotiveError('');
              }
            }}
          />
          {blockMotiveError && (
            <span className="visitante-error">{blockMotiveError}</span>
          )}
        </div>
        <div className="modal-actions">
          <button
            className="btn-visitor-secondary"
            onClick={confirmBlockWithMotive}
            disabled={isSavingBlockMotive}
          >
            {isSavingBlockMotive ? 'Salvando...' : 'Confirmar bloqueio'}
          </button>
        </div>
      </Modal>

      <div className={confirmContext.isBlocking ? 'visitor-block-modal' : 'visitor-unblock-modal'}>
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          closeModal={closeConfirmModal}
          onConfirm={confirmBlockAction}
          icon={confirmContext.icon}
          mainMessage={confirmContext.main}
          secondaryMessage={confirmContext.secondary}
        />
      </div>
    </div>
  );
}

export default ListVisitantes;
